import axios from 'axios';

const apiBaseUrl = import.meta.env.VITE_API_URL || '';

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    // Names this app in the platform audit log. Without it the server has to
    // guess a surface from the path, and admin work done outside /admin
    // routes would read as ordinary dashboard activity.
    'X-GuideAI-Surface': 'admin',
  },
});

// Request interceptor: attach JWT Bearer token
apiClient.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('guideai_jwt_tokens');
    if (raw) {
      const tokens = JSON.parse(raw);
      if (tokens?.access_token) {
        config.headers.Authorization = `Bearer ${tokens.access_token}`;
        return config;
      }
    }
  } catch {
    // fall through
  }
  return config;
});

// Track if we're already refreshing to avoid infinite loops
let isRefreshing = false;

// Response interceptor: handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry && !isRefreshing) {
      original._retry = true;

      try {
        const raw = localStorage.getItem('guideai_jwt_tokens');
        if (raw) {
          const tokens = JSON.parse(raw);
          if (tokens?.refresh_token) {
            isRefreshing = true;
            const { data } = await axios.post(
              `${apiBaseUrl}/api/v1/auth/refresh`,
              { refresh_token: tokens.refresh_token },
              { headers: { 'Content-Type': 'application/json' } },
            );
            localStorage.setItem(
              'guideai_jwt_tokens',
              JSON.stringify({
                access_token: data.access_token,
                refresh_token: data.refresh_token,
              }),
            );
            isRefreshing = false;
            original.headers.Authorization = `Bearer ${data.access_token}`;
            return apiClient(original);
          }
        }
      } catch {
        isRefreshing = false;
      }

      // Refresh failed or no tokens — clear and redirect
      localStorage.removeItem('guideai_jwt_tokens');
      localStorage.removeItem('guideai_jwt_user');
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
